/**
 * ============================================================================
 * AI Recommendations Panel
 * ============================================================================
 *
 * Full-featured panel for displaying AI-generated carbon reduction
 * recommendations with:
 * - Generate/refresh button with loading states
 * - Recommendation cards with impact badges
 * - Status management (accept, complete, dismiss)
 * - Summary statistics
 * - Cache indicator
 *
 * Uses tRPC hooks to interact with the recommendations API.
 *
 * ============================================================================
 */

import { useState } from "react";
import RecommendationDetailsDrawer from "./RecommendationDetailsDrawer";
import { trpc } from "@/lib/trpc";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  Sparkles,
  RefreshCw,
  Loader2,
  CheckCircle2,
  XCircle,
  PlayCircle,
  Undo2,
  TrendingDown,
  Zap,
  Truck,
  Trash2,
  Droplets,
  ShoppingCart,
  MoreHorizontal,
  AlertTriangle,
  Info,
  Eye,
} from "lucide-react";

// ============================================================================
// TYPES
// ============================================================================

interface Recommendation {
  id: string;
  title: string;
  description: string;
  category: string;
  impact_level: string;
  estimated_reduction_percentage: number | null;
  estimated_reduction_tco2e: number | null;
  scope: string | null;
  priority: number;
  status: string;
  generated_by: string;
  created_at: string;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const CATEGORY_CONFIG: Record<
  string,
  { icon: typeof Zap; label: string; color: string }
> = {
  energy: {
    icon: Zap,
    label: "Energie",
    color: "bg-amber-100 text-amber-800 dark:bg-amber-950/20 dark:text-amber-400",
  },
  transport: {
    icon: Truck,
    label: "Transport",
    color: "bg-blue-100 text-blue-800 dark:bg-blue-950/20 dark:text-blue-400",
  },
  waste: {
    icon: Trash2,
    label: "Dechets",
    color:
      "bg-orange-100 text-orange-800 dark:bg-orange-950/20 dark:text-orange-400",
  },
  water: {
    icon: Droplets,
    label: "Eau",
    color: "bg-cyan-100 text-cyan-800 dark:bg-cyan-950/20 dark:text-cyan-400",
  },
  purchases: {
    icon: ShoppingCart,
    label: "Achats",
    color:
      "bg-purple-100 text-purple-800 dark:bg-purple-950/20 dark:text-purple-400",
  },
  other: {
    icon: MoreHorizontal,
    label: "Autre",
    color: "bg-gray-100 text-gray-800 dark:bg-gray-950/20 dark:text-gray-400",
  },
};

const IMPACT_CONFIG: Record<
  string,
  { label: string; color: string; emoji: string }
> = {
  high: {
    label: "Impact fort",
    color:
      "bg-red-100 text-red-800 dark:bg-red-950/20 dark:text-red-400",
    emoji: "🔴",
  },
  medium: {
    label: "Impact moyen",
    color:
      "bg-yellow-100 text-yellow-800 dark:bg-yellow-950/20 dark:text-yellow-400",
    emoji: "🟡",
  },
  low: {
    label: "Impact faible",
    color:
      "bg-green-100 text-green-800 dark:bg-green-950/20 dark:text-green-400",
    emoji: "🟢",
  },
};

const STATUS_CONFIG: Record<
  string,
  { label: string; color: string }
> = {
  pending: { label: "En attente", color: "bg-gray-100 text-gray-700" },
  in_progress: { label: "En cours", color: "bg-blue-100 text-blue-700" },
  completed: { label: "Termine", color: "bg-green-100 text-green-700" },
  dismissed: { label: "Ecarte", color: "bg-red-100 text-red-700" },
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

interface RecommendationsPanelProps {
  periodId?: string;
  compact?: boolean;
}

export default function RecommendationsPanel({
  periodId,
  compact = false,
}: RecommendationsPanelProps) {
  const [statusFilter, setStatusFilter] = useState<string | undefined>(
    undefined
  );
  const [selectedRecommendation, setSelectedRecommendation] = useState<{
    id: string;
    title: string;
  } | null>(null);

  // Queries
  const recommendationsQuery = trpc.recommendations.list.useQuery(
    { periodId, status: statusFilter as any },
    { retry: 1 }
  );

  const summaryQuery = trpc.recommendations.summary.useQuery(
    { periodId },
    { retry: 1 }
  );

  // Mutations
  const generateMutation = trpc.recommendations.generate.useMutation({
    onSuccess: () => {
      recommendationsQuery.refetch();
      summaryQuery.refetch();
    },
  });

  const updateStatusMutation =
    trpc.recommendations.updateStatus.useMutation({
      onSuccess: () => {
        recommendationsQuery.refetch();
        summaryQuery.refetch();
      },
    });

  // Handlers
  const handleGenerate = (forceRegenerate = false) => {
    generateMutation.mutate({ periodId, forceRegenerate });
  };

  const handleStatusUpdate = (
    id: string,
    status: "pending" | "in_progress" | "completed" | "dismissed"
  ) => {
    updateStatusMutation.mutate({ id, status });
  };

  const recommendations = recommendationsQuery.data || [];
  const summary = summaryQuery.data;
  const isLoading =
    recommendationsQuery.isLoading || summaryQuery.isLoading;
  const isGenerating = generateMutation.isPending;

  // ========================================================================
  // COMPACT MODE (for Dashboard inline)
  // ========================================================================
  if (compact) {
    return (
      <div className="space-y-4">
        {/* Header with generate button */}
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold tracking-tight">
            AI Recommendations 🤖
          </h2>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleGenerate(false)}
            disabled={isGenerating}
          >
            {isGenerating ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4" />
                Generate
              </>
            )}
          </Button>
        </div>

        {/* Error state */}
        {generateMutation.isError && (
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="p-4 flex items-center gap-2 text-amber-800">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              <p className="text-sm">
                {generateMutation.error?.message ||
                  "Unable to generate recommendations. Make sure you have emission data entered."}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Loading state */}
        {isGenerating && (
          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="p-6 flex flex-col items-center gap-3">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              <div className="text-center">
                <p className="font-medium text-blue-800">
                  Analyzing your emissions data...
                </p>
                <p className="text-sm text-blue-600 mt-1">
                  Claude AI is generating personalized recommendations
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Recommendations grid */}
        {!isGenerating && (
          <div className="grid gap-4 md:grid-cols-2">
            {recommendations.length > 0 ? (
              recommendations
                .filter((r) => r.status !== "dismissed")
                .slice(0, 4)
                .map((rec) => (
                  <RecommendationCard
                    key={rec.id}
                    recommendation={rec as Recommendation}
                    onStatusUpdate={handleStatusUpdate}
                    isUpdating={updateStatusMutation.isPending}
                    compact
                    onViewDetails={(id, title) => setSelectedRecommendation({ id, title })}
                  />
                ))
            ) : !isLoading ? (
              <Card className="md:col-span-2">
                <CardContent className="p-6 text-center">
                  <Sparkles className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                  <p className="text-muted-foreground">
                    No recommendations yet. Click "Generate" to get
                    AI-powered suggestions for reducing your carbon
                    footprint.
                  </p>
                </CardContent>
              </Card>
            ) : null}
          </div>
        )}

        {/* Level 2 Details Drawer */}
        <RecommendationDetailsDrawer
          recommendationId={selectedRecommendation?.id || null}
          recommendationTitle={selectedRecommendation?.title}
          open={!!selectedRecommendation}
          onOpenChange={(open) => {
            if (!open) setSelectedRecommendation(null);
          }}
        />
      </div>
    );
  }

  // ========================================================================
  // FULL MODE (dedicated page)
  // ========================================================================
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Sparkles className="h-8 w-8 text-amber-500" />
            AI Recommendations
          </h1>
          <p className="text-muted-foreground mt-1">
            Personalized carbon reduction recommendations powered by AI
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => handleGenerate(true)}
            disabled={isGenerating}
          >
            {isGenerating ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            Regenerate
          </Button>
          <Button onClick={() => handleGenerate(false)} disabled={isGenerating}>
            {isGenerating ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4" />
                Generate
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && summary.hasRecommendations && (
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-muted-foreground">Total</div>
              <div className="text-2xl font-bold">{summary.total}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-muted-foreground">In Progress</div>
              <div className="text-2xl font-bold text-blue-600">
                {summary.inProgress}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-muted-foreground">Completed</div>
              <div className="text-2xl font-bold text-green-600">
                {summary.completed}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-muted-foreground flex items-center gap-1">
                <TrendingDown className="h-3 w-3" />
                Reduction Potential
              </div>
              <div className="text-2xl font-bold text-emerald-600">
                {summary.totalReductionPotential.toFixed(1)} tCO2e
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Completion progress */}
      {summary && summary.total > 0 && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">
                Implementation Progress
              </span>
              <span className="text-sm text-muted-foreground">
                {summary.completed}/{summary.total - summary.dismissed}{" "}
                completed
              </span>
            </div>
            <Progress
              value={
                ((summary.completed) /
                  Math.max(summary.total - summary.dismissed, 1)) *
                100
              }
            />
          </CardContent>
        </Card>
      )}

      {/* Filter tabs */}
      <div className="flex gap-2 flex-wrap">
        {[
          { key: undefined, label: "All" },
          { key: "pending", label: "Pending" },
          { key: "in_progress", label: "In Progress" },
          { key: "completed", label: "Completed" },
          { key: "dismissed", label: "Dismissed" },
        ].map((filter) => (
          <Button
            key={filter.label}
            variant={statusFilter === filter.key ? "default" : "outline"}
            size="sm"
            onClick={() => setStatusFilter(filter.key)}
          >
            {filter.label}
            {filter.key === undefined && summary
              ? ` (${summary.total})`
              : ""}
          </Button>
        ))}
      </div>

      {/* Error state */}
      {generateMutation.isError && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4 flex items-center gap-2 text-amber-800">
            <AlertTriangle className="h-4 w-4 flex-shrink-0" />
            <p className="text-sm">
              {generateMutation.error?.message ||
                "Unable to generate recommendations."}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Generating state */}
      {isGenerating && (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="p-8 flex flex-col items-center gap-4">
            <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
            <div className="text-center">
              <p className="text-lg font-medium text-blue-800">
                Analyzing your emissions data...
              </p>
              <p className="text-sm text-blue-600 mt-1">
                Claude AI is generating personalized recommendations for
                your company
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recommendations list */}
      {!isGenerating && (
        <div className="space-y-4">
          {recommendations.length > 0 ? (
            recommendations.map((rec) => (
              <RecommendationCard
                key={rec.id}
                recommendation={rec as Recommendation}
                onStatusUpdate={handleStatusUpdate}
                isUpdating={updateStatusMutation.isPending}
                onViewDetails={(id, title) => setSelectedRecommendation({ id, title })}
              />
            ))
          ) : !isLoading ? (
            <Card>
              <CardContent className="p-12 text-center">
                <Sparkles className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">
                  No recommendations yet
                </h3>
                <p className="text-muted-foreground mb-4">
                  Click "Generate" to get AI-powered suggestions for
                  reducing your carbon footprint.
                </p>
                <Button onClick={() => handleGenerate(false)}>
                  <Sparkles className="h-4 w-4" />
                  Generate Recommendations
                </Button>
              </CardContent>
            </Card>
          ) : null}
        </div>
      )}

      {/* Cache indicator */}
      {generateMutation.data?.cached && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Info className="h-4 w-4" />
          <span>
            Showing cached recommendations from{" "}
            {new Date(
              generateMutation.data.generatedAt
            ).toLocaleDateString("fr-FR")}
            . Click "Regenerate" for fresh analysis.
          </span>
        </div>
      )}

      {/* Level 2 Details Drawer */}
      <RecommendationDetailsDrawer
        recommendationId={selectedRecommendation?.id || null}
        recommendationTitle={selectedRecommendation?.title}
        open={!!selectedRecommendation}
        onOpenChange={(open) => {
          if (!open) setSelectedRecommendation(null);
        }}
      />
    </div>
  );
}

// ============================================================================
// RECOMMENDATION CARD SUB-COMPONENT
// ============================================================================

interface RecommendationCardProps {
  recommendation: Recommendation;
  onStatusUpdate: (
    id: string,
    status: "pending" | "in_progress" | "completed" | "dismissed"
  ) => void;
  isUpdating: boolean;
  compact?: boolean;
  onViewDetails?: (id: string, title: string) => void;
}

function RecommendationCard({
  recommendation: rec,
  onStatusUpdate,
  isUpdating,
  compact = false,
  onViewDetails,
}: RecommendationCardProps) {
  const category = CATEGORY_CONFIG[rec.category] || CATEGORY_CONFIG.other;
  const impact = IMPACT_CONFIG[rec.impact_level] || IMPACT_CONFIG.medium;
  const status = STATUS_CONFIG[rec.status] || STATUS_CONFIG.pending;
  const CategoryIcon = category.icon;

  if (compact) {
    return (
      <Card className="hover:shadow-md transition-shadow">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-muted">
              <CategoryIcon className="h-5 w-5 text-muted-foreground" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <h3 className="font-semibold text-sm truncate">
                  {rec.title}
                </h3>
                <Badge variant="secondary" className={impact.color}>
                  {impact.label}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground line-clamp-2 mb-2">
                {rec.description}
              </p>
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="outline" className={category.color}>
                  {category.label}
                </Badge>
                {rec.estimated_reduction_tco2e != null &&
                  rec.estimated_reduction_tco2e > 0 && (
                    <span className="text-xs text-emerald-600 font-medium flex items-center gap-1">
                      <TrendingDown className="h-3 w-3" />-
                      {Number(rec.estimated_reduction_tco2e).toFixed(1)}{" "}
                      tCO2e
                    </span>
                  )}
                {onViewDetails && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onViewDetails(rec.id, rec.title)}
                    className="text-primary hover:text-primary h-6 px-2 text-xs ml-auto"
                  >
                    <Eye className="h-3 w-3 mr-1" />
                    Détail
                  </Button>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      className={`transition-shadow hover:shadow-md ${
        rec.status === "completed"
          ? "border-green-200 bg-green-50/30"
          : rec.status === "dismissed"
            ? "opacity-60"
            : ""
      }`}
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-muted flex-shrink-0">
              <CategoryIcon className="h-5 w-5 text-muted-foreground" />
            </div>
            <div>
              <CardTitle className="text-base">{rec.title}</CardTitle>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <Badge variant="secondary" className={impact.color}>
                  {impact.label}
                </Badge>
                <Badge variant="outline" className={category.color}>
                  {category.label}
                </Badge>
                {rec.scope && (
                  <Badge variant="outline" className="text-xs">
                    {rec.scope}
                  </Badge>
                )}
                <Badge
                  variant="secondary"
                  className={status.color}
                >
                  {status.label}
                </Badge>
              </div>
            </div>
          </div>
          <div className="text-right flex-shrink-0">
            {rec.estimated_reduction_tco2e != null &&
              rec.estimated_reduction_tco2e > 0 && (
                <div className="text-lg font-bold text-emerald-600 flex items-center gap-1">
                  <TrendingDown className="h-4 w-4" />-
                  {Number(rec.estimated_reduction_tco2e).toFixed(1)} tCO2e
                </div>
              )}
            {rec.estimated_reduction_percentage != null &&
              rec.estimated_reduction_percentage > 0 && (
                <div className="text-xs text-muted-foreground">
                  -{Number(rec.estimated_reduction_percentage).toFixed(0)}%
                  reduction
                </div>
              )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="pb-3">
        <CardDescription className="text-sm leading-relaxed">
          {rec.description}
        </CardDescription>
      </CardContent>
      <CardFooter className="flex items-center justify-between pt-0 gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="text-xs text-muted-foreground">
            Priority: {rec.priority}/100
          </div>
          {onViewDetails && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onViewDetails(rec.id, rec.title)}
              className="text-primary hover:text-primary"
            >
              <Eye className="h-3 w-3" />
              Voir le détail
            </Button>
          )}
        </div>
        <div className="flex gap-2">
          {rec.status === "pending" && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onStatusUpdate(rec.id, "dismissed")}
                disabled={isUpdating}
              >
                <XCircle className="h-3 w-3" />
                Dismiss
              </Button>
              <Button
                size="sm"
                onClick={() => onStatusUpdate(rec.id, "in_progress")}
                disabled={isUpdating}
              >
                <PlayCircle className="h-3 w-3" />
                Start
              </Button>
            </>
          )}
          {rec.status === "in_progress" && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onStatusUpdate(rec.id, "pending")}
                disabled={isUpdating}
              >
                <Undo2 className="h-3 w-3" />
                Pause
              </Button>
              <Button
                size="sm"
                className="bg-green-600 hover:bg-green-700"
                onClick={() => onStatusUpdate(rec.id, "completed")}
                disabled={isUpdating}
              >
                <CheckCircle2 className="h-3 w-3" />
                Complete
              </Button>
            </>
          )}
          {rec.status === "completed" && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onStatusUpdate(rec.id, "pending")}
              disabled={isUpdating}
            >
              <Undo2 className="h-3 w-3" />
              Reopen
            </Button>
          )}
          {rec.status === "dismissed" && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onStatusUpdate(rec.id, "pending")}
              disabled={isUpdating}
            >
              <Undo2 className="h-3 w-3" />
              Restore
            </Button>
          )}
        </div>
      </CardFooter>
    </Card>
  );
}
